import * as Sentry from "@sentry/react";
import { env } from "config/env";
import {
  hasIgnoredFrame,
  hasIgnoredMessage,
  ignoredFrameUrls,
  ignoredMessages,
  isDomReparentingError,
  isEmptyObjectRejection,
  isServerApiError,
  normalizeSentryEvent,
} from "utils/errorReporting";

const ignoredStacks = ["zoom.us", "@quillsql", "/widget-next/"];
const ignoredUrls = ["zoom.us", "/api/dashboard/?schema-context"];

Sentry.init({
  enabled: !env.IS_LOCAL,
  dsn: env.REACT_APP_SENTRY_DSN,
  environment: env.REACT_APP_ENV,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
  dataCollection: { userInfo: true },

  beforeSend(event, hint) {
    const recentBreadcrumbs = event.breadcrumbs?.slice(-3) ?? [];
    if (recentBreadcrumbs.some(({ message }) => message && ignoredUrls.some((url) => message.includes(url)))) {
      return null;
    }
    if (hasIgnoredMessage(event, hint, ignoredMessages)) {
      return null;
    }
    if (isDomReparentingError(event)) {
      return null;
    }
    if (isServerApiError(hint)) {
      return null;
    }
    if (isEmptyObjectRejection(hint)) {
      return null;
    }
    const originalException = hint?.originalException instanceof Error ? hint.originalException : null;
    if (originalException?.stack && ignoredStacks.some((stack) => originalException.stack?.includes(stack))) {
      return null;
    }
    if (hasIgnoredFrame(event, ignoredFrameUrls)) {
      return null;
    }
    return normalizeSentryEvent(event, hint);
  },

  // tanstackRouterBrowserTracingIntegration needs the router instance, so it's added in routing/router.tsx
  // after the router is created (caveat #2). The rest are instance-free and init here.
  integrations: [Sentry.launchDarklyIntegration(), Sentry.extraErrorDataIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 0.05,
  // profile sampled traces (gated by tracesSampleRate); relative to the session sample rate
  profileLifecycle: "trace",
  profileSessionSampleRate: 0.05,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  // tracePropagationTargets: [env.REACT_APP_SG_API_URL],
});
